/**
 * PerformanceSnapshotPanel — Iter 170 (Danny rep_activity_drill_down_spec.md,
 * Josh approved 2026-06-05).
 *
 * Slide-out panel that opens on the right of the Rep Activity page. Level 1
 * of the new two-level drill-down architecture. Shows aggregated,
 * scannable performance data for ONE rep over the selected date range.
 *
 * Five sections per Danny's spec:
 *   1. Call volume overview
 *   2. Contact role breakdown (powered by iter 169 + iter 170 role_breakdown)
 *   3. Call time & duration distribution
 *   4. Idle time (PLACEHOLDER — pending Item A: dialer_sessions schema)
 *   5. Call quality + AI scores
 *
 * Closes via X or "← Close Snapshot". "View full activity →" closes the
 * panel and reveals the Activity Detail (call-by-call list) underneath.
 */
import React, { useMemo, useEffect, useState } from "react";
import { X, ChevronRight, ChevronDown, Sparkles, Clock, Phone, Users, BarChart3, AlertTriangle, ExternalLink } from "lucide-react";
import { CONTACT_ROLES, SNAPSHOT_DISPLAY_ORDER, colorForRole } from "@/lib/contactRoles";
import { SUB_STATUSES } from "@/lib/pipelineStages";

const fmtSecs = (s) => {
  s = Number(s || 0);
  if (s <= 0) return "—";
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return r ? `${h}h ${m}m ${r}s` : m > 0 ? `${h}h ${m}m` : `${h}h`;
  return r ? `${m}m ${r}s` : `${m}m`;
};

const fmtPct = (n) => `${Math.round(n)}%`;

// Section 1 — Call volume stacked bar
function CallVolumeBar({ connects, voicemails, no_answers, total }) {
  if (!total) return null;
  const pc = (n) => (n / total) * 100;
  return (
    <div data-testid="snapshot-volume-bar" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", height: 12, borderRadius: 4, overflow: "hidden", border: "0.5px solid #e2e8f0" }}>
        <div style={{ width: `${pc(connects)}%`, background: "#2563eb" }} title={`Live contacts: ${connects}`} />
        <div style={{ width: `${pc(voicemails)}%`, background: "#d97706" }} title={`Voicemails: ${voicemails}`} />
        <div style={{ width: `${pc(no_answers)}%`, background: "#cbd5e1" }} title={`No answer: ${no_answers}`} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginTop: 4, color: "#64748b" }}>
        <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#2563eb", borderRadius: 2, marginRight: 4 }} /> Live contacts</span>
        <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#d97706", borderRadius: 2, marginRight: 4 }} /> Voicemails</span>
        <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#cbd5e1", borderRadius: 2, marginRight: 4 }} /> No answer</span>
      </div>
    </div>
  );
}

// Section 2 — Contact role breakdown
function ContactRoleBreakdown({ roleBreakdown, connects }) {
  // Rows = each role with non-zero count, ordered by SNAPSHOT_DISPLAY_ORDER.
  // We INTENTIONALLY ignore `untagged` here per spec (Section 2 says "Only
  // counts calls where a live contact was made").
  const rows = useMemo(() => {
    if (!roleBreakdown) return [];
    return SNAPSHOT_DISPLAY_ORDER
      .filter((id) => id !== "untagged")
      .map((id) => {
        const count = roleBreakdown[id] || 0;
        const meta = CONTACT_ROLES.find((r) => r.id === id);
        return { id, count, label: meta?.label || id, color: colorForRole(id) };
      })
      .filter((r) => r.count > 0);
  }, [roleBreakdown]);

  const total = rows.reduce((acc, r) => acc + r.count, 0);

  if (!total) {
    return (
      <div data-testid="snapshot-role-empty" style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", padding: "8px 0" }}>
        No live contacts logged with a role yet — reps should pick "Who did you speak with?" on the disposition modal.
      </div>
    );
  }

  return (
    <div data-testid="snapshot-role-breakdown" style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
      <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>
        Who was reached ({total} live contact{total === 1 ? "" : "s"})
      </div>
      {rows.map((r) => {
        const pct = total ? (r.count / total) * 100 : 0;
        return (
          <div key={r.id} data-testid={`snapshot-role-row-${r.id}`} style={{ display: "grid", gridTemplateColumns: "150px 1fr 50px 40px", alignItems: "center", gap: 8, fontSize: 11 }}>
            <span style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.label}>{r.label}</span>
            <div style={{ height: 8, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: r.color, borderRadius: 999 }} />
            </div>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#0f172a", fontWeight: 500 }}>{r.count}</span>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 10, color: "#64748b" }}>{fmtPct(pct)}</span>
          </div>
        );
      })}
    </div>
  );
}

// Iter 174.2 (Danny — granular Outcome). Sub-status tier breakdown for
// the Performance Snapshot. Reads each call's `lead_sub_status` (added
// to the /admin/rep-activity/{agent_id}/calls payload) and buckets them
// by the same tiers the Lead Focus sub-status picker uses.
const SUB_TIER_META = [
  { key: "gatekeeper", label: "Gatekeeper (Front Desk · MA · Coordinators)", color: "#64748b" },
  { key: "admin",      label: "Administrative (Office Mgr · Practice Mgr · Billing · HR)", color: "#1e40af" },
  { key: "clinical",   label: "Physician / Clinical (MD · DO · NP · PA · Med Director)", color: "#166534" },
  { key: "executive",  label: "Executive (CEO · CFO · COO · CMO · VPs)", color: "#6d28d9" },
  // Wave 2 / B4 — `untagged` row absorbs live-contact calls that the rep
  // hasn't yet labeled with a sub-status. Without it, the tier rows
  // would always undercount the connect-rate live-contact tile.
  { key: "untagged",   label: "Connected — sub-status not yet tagged", color: "#cbd5e1" },
];
// Wave 2 / B4 — `no_contact` is intentionally NOT a tier in the
// breakdown anymore: per spec, only Category-A (live contact) outcomes
// count toward connect rate, and "Left Voicemail" / "Needs Follow-Up"
// are no_contact outcomes, not connect outcomes. They live in the call
// volume bar instead.
// Build a lookup once at module load from the canonical sub-status list.
const SUB_TIER_LOOKUP = (() => {
  const out = {};
  for (const s of (SUB_STATUSES?.contacted || [])) out[s.value] = s.tier;
  return out;
})();
// Wave 2 / B4 — outcomes that count as a "live contact" / Category A
// must match the backend aggregation in /admin/rep-activity (see
// server.py: `connects = outcome in {connected, demo-booked}`). Keeping
// this in lock-step is what makes the tier rows sum exactly to the
// connect-rate live-contact tile.
const LIVE_CONTACT_OUTCOMES = new Set(["connected", "demo-booked"]);

function SubStatusTierBreakdown({ calls, expectedTotal }) {
  // Iter 174.7 (Danny B6 — expandable tier drill-through).
  // Each tier row is now click-to-expand. Expanding shows the first
  // 5 leads in that tier with their company / sub-status / outcome.
  // "View all (N) →" link drops the user into a filtered Reports view
  // when the tier has more than 5 leads.
  const [expanded, setExpanded] = React.useState(null);

  // Build the tier → calls map so we can show drill-through samples
  // per tier without filtering on every render.
  // Wave 2 / B4 — restrict to live-contact calls so the row totals match
  // `live contacts` in the connect-rate tile. Calls whose sub-status
  // isn't recognised (or is empty) land in `untagged` instead of being
  // silently dropped.
  const callsByTier = React.useMemo(() => {
    const out = { gatekeeper: [], admin: [], clinical: [], executive: [], untagged: [] };
    (calls || []).forEach((call) => {
      const outcome = String(call.outcome || "").toLowerCase();
      if (!LIVE_CONTACT_OUTCOMES.has(outcome)) return; // skip non-connects
      const sub = call.lead_sub_status;
      const tier = sub ? SUB_TIER_LOOKUP[sub] : null;
      if (tier && Object.prototype.hasOwnProperty.call(out, tier) && tier !== "no_contact") {
        out[tier].push(call);
      } else {
        out.untagged.push(call);
      }
    });
    return out;
  }, [calls]);
  const counts = React.useMemo(() => {
    const c = { gatekeeper: 0, admin: 0, clinical: 0, executive: 0, untagged: 0 };
    Object.keys(callsByTier).forEach((k) => { c[k] = callsByTier[k].length; });
    return c;
  }, [callsByTier]);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return null;
  return (
    <div data-testid="snapshot-substatus-breakdown" style={{ marginTop: 14, paddingTop: 12, borderTop: "0.5px dashed #e2e8f0" }}>
      <div style={{ fontSize: 10, color: "#64748b", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>
          {/* Wave 2 / B4 — heading now explicitly references the live-contact
              tile so the alignment is obvious to the manager. */}
          Live-contact sub-status tiers ({total} live contact{total === 1 ? "" : "s"}) — click a row to drill in
        </span>
        {typeof expectedTotal === "number" && expectedTotal !== total && (
          <span
            data-testid="snapshot-substatus-mismatch-warning"
            title={`Connect-rate tile reports ${expectedTotal} live contact${expectedTotal === 1 ? "" : "s"} but the tier rows sum to ${total}. Refresh the page or expand the date range.`}
            style={{ fontSize: 10, color: "#b45309", fontWeight: 600 }}
          >⚠ tile reads {expectedTotal}</span>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {SUB_TIER_META.map((m) => {
          const count = counts[m.key] || 0;
          const pct = total ? (count / total) * 100 : 0;
          const isOpen = expanded === m.key;
          const tierCalls = callsByTier[m.key] || [];
          return (
            <div key={m.key}>
              <button
                data-testid={`snapshot-substatus-row-${m.key}`}
                onClick={() => count > 0 && setExpanded(isOpen ? null : m.key)}
                disabled={count === 0}
                style={{
                  width: "100%", display: "grid",
                  gridTemplateColumns: "12px 240px 1fr 50px 40px",
                  alignItems: "center", gap: 8, fontSize: 11,
                  padding: "3px 4px", borderRadius: 4,
                  background: isOpen ? "#f1f5f9" : "transparent",
                  border: "none", cursor: count > 0 ? "pointer" : "default",
                  fontFamily: "inherit", textAlign: "left",
                  opacity: count === 0 ? 0.55 : 1,
                  transition: "background .12s",
                }}
                onMouseEnter={(e) => { if (count > 0 && !isOpen) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ color: "#94a3b8", fontSize: 10 }}>{count > 0 ? (isOpen ? "▼" : "▶") : "·"}</span>
                <span style={{ color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.label}>{m.label}</span>
                <div style={{ height: 8, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: m.color, borderRadius: 999 }} />
                </div>
                <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#0f172a", fontWeight: 500 }}>{count}</span>
                <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 10, color: "#64748b" }}>{count ? `${Math.round(pct)}%` : "—"}</span>
              </button>
              {isOpen && (
                <div
                  data-testid={`snapshot-substatus-drill-${m.key}`}
                  style={{
                    marginLeft: 20, marginTop: 4, marginBottom: 6,
                    padding: "8px 10px", background: "#fafafa",
                    border: "0.5px solid #e2e8f0", borderRadius: 6,
                    display: "flex", flexDirection: "column", gap: 5,
                  }}
                >
                  {tierCalls.slice(0, 5).map((call, i) => {
                    const name = call.lead_company || call.lead_name || "(no company)";
                    const sub = (call.lead_sub_status || "").replace(/^Contacted —\s*/i, "");
                    return (
                      <div
                        key={`${call.id || i}`}
                        data-testid={`snapshot-substatus-drill-row-${m.key}-${i}`}
                        style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}
                      >
                        <span style={{ flex: 1, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
                          {name}
                        </span>
                        <span style={{ fontSize: 9.5, color: "#64748b" }} title={sub}>{sub}</span>
                        {call.lead_id && (
                          <a
                            href={call.ehi_client_id ? `/ehi-clients/${call.ehi_client_id}` : `/lead-focus/${call.lead_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`snapshot-substatus-drill-review-${m.key}-${i}`}
                            style={{ fontSize: 10, color: "#2563eb", textDecoration: "none", fontWeight: 500 }}
                          >
                            Review →
                          </a>
                        )}
                      </div>
                    );
                  })}
                  {tierCalls.length > 5 && (
                    <a
                      href={`/admin/rep-activity?tier=${m.key}`}
                      data-testid={`snapshot-substatus-drill-viewall-${m.key}`}
                      style={{ fontSize: 10.5, color: "#2563eb", marginTop: 3, fontWeight: 500 }}
                    >
                      View all {tierCalls.length} →
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Section 3 — Duration distribution (5 buckets per Danny spec).
// Compute client-side from the per-call rows returned by
// /admin/rep-activity/{agent_id}/calls.
function DurationDistribution({ calls }) {
  const buckets = useMemo(() => {
    const b = [
      { label: "Under 30s",   min: 0,    max: 30,   compliance: true,  count: 0 },
      { label: "30s – 2min",  min: 30,   max: 120,  compliance: false, count: 0 },
      { label: "2 – 5min",    min: 120,  max: 300,  compliance: false, count: 0 },
      { label: "5 – 10min",   min: 300,  max: 600,  compliance: false, count: 0 },
      { label: "Over 10min",  min: 600,  max: Infinity, compliance: false, count: 0 },
    ];
    (calls || []).forEach((c) => {
      const s = c.duration_sec || c.duration_seconds || 0;
      const bucket = b.find((x) => s >= x.min && s < x.max);
      if (bucket) bucket.count++;
    });
    return b;
  }, [calls]);

  const total = buckets.reduce((acc, b) => acc + b.count, 0);

  if (!total) {
    return <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", padding: "6px 0" }}>No call duration data yet.</div>;
  }

  return (
    <div data-testid="snapshot-duration-distribution" style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 8 }}>
      {buckets.map((b) => {
        const pct = total ? (b.count / total) * 100 : 0;
        const isShort = b.compliance && b.count > 0;
        return (
          <div key={b.label} data-testid={`snapshot-duration-${b.label.replace(/\s|–/g, "-").toLowerCase()}`} style={{ display: "grid", gridTemplateColumns: "100px 1fr 50px 40px", alignItems: "center", gap: 8, fontSize: 11 }}>
            <span style={{ color: isShort ? "#b45309" : "#475569" }}>{b.label}</span>
            <div style={{ height: 8, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: isShort ? "#f59e0b" : "#2563eb", borderRadius: 999 }} />
            </div>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#0f172a", fontWeight: 500 }}>{b.count}</span>
            <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 10, color: "#64748b" }}>{fmtPct(pct)}</span>
            {isShort && (
              <div style={{ gridColumn: "1 / -1", fontSize: 10, color: "#b45309", marginTop: -2, display: "flex", alignItems: "center", gap: 4 }} data-testid="snapshot-duration-compliance-flag">
                <AlertTriangle style={{ width: 10, height: 10 }} /> {b.count} short call{b.count === 1 ? "" : "s"} flagged for compliance review
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Section 4 — Idle time (PLACEHOLDER, Item A pending Josh).
function IdleTimePlaceholder() {
  return (
    <div data-testid="snapshot-idle-placeholder" style={{ background: "#f8fafc", border: "0.5px dashed #cbd5e1", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: "#64748b", display: "flex", gap: 8, alignItems: "flex-start" }}>
      <Clock style={{ width: 12, height: 12, color: "#94a3b8", marginTop: 1, flexShrink: 0 }} />
      <div>
        <div style={{ fontWeight: 500, color: "#475569", marginBottom: 2 }}>Idle time — coming soon</div>
        <div>This section will show total dialer session time, idle gaps, longest break, and a 90s-idle coaching flag once dialer-session tracking lands (Item A pending Josh approval per <code style={{ fontFamily: "var(--font)" }}>rep_activity_drill_down_spec.md</code>).</div>
      </div>
    </div>
  );
}

// Section 5 — Call quality + AI scores
function CallQualityBlock({ calls, callsLoading }) {
  const stats = useMemo(() => {
    const scored = (calls || []).filter((c) => typeof c.agent_score === "number" && c.agent_score > 0);
    if (!scored.length) return null;
    const scores = scored.map((c) => c.agent_score);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    const min = Math.min(...scores);
    const max = Math.max(...scores);
    const buckets = { excellent: 0, good: 0, developing: 0, coaching: 0 };
    scores.forEach((s) => {
      if (s >= 85) buckets.excellent++;
      else if (s >= 70) buckets.good++;
      else if (s >= 55) buckets.developing++;
      else buckets.coaching++;
    });
    return { avg, min, max, total: scores.length, buckets };
  }, [calls]);

  if (callsLoading) {
    return <div data-testid="snapshot-quality-loading" style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>Loading call scores…</div>;
  }
  if (!stats) {
    return <div data-testid="snapshot-quality-empty" style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No scored calls yet for this period.</div>;
  }
  const grade = stats.avg >= 85 ? "Excellent" : stats.avg >= 70 ? "Good" : stats.avg >= 55 ? "Developing" : "Coaching needed";
  const gradeColor = stats.avg >= 85 ? "#059669" : stats.avg >= 70 ? "#2563eb" : stats.avg >= 55 ? "#d97706" : "#dc2626";

  const pct = (n) => stats.total ? Math.round((n / stats.total) * 100) : 0;

  return (
    <div data-testid="snapshot-quality-block" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6, fontSize: 11 }}>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", background: "#f8fafc", border: "0.5px solid #e2e8f0", borderRadius: 8 }}>
        <span style={{ color: "#475569" }}>Avg agent call score</span>
        <span style={{ fontWeight: 600, color: gradeColor, fontVariantNumeric: "tabular-nums" }}>{Math.round(stats.avg)} <span style={{ fontSize: 10, fontWeight: 400 }}>· {grade}</span></span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748b", padding: "0 10px" }}>
        <span>Score range</span>
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#475569" }}>{stats.min} – {stats.max}</span>
      </div>
      <div style={{ marginTop: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 10px" }}>
          <span style={{ color: "#059669" }}>Excellent (85+)</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{stats.buckets.excellent} <span style={{ fontSize: 10, color: "#94a3b8" }}>({pct(stats.buckets.excellent)}%)</span></span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 10px" }}>
          <span style={{ color: "#2563eb" }}>Good (70–84)</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{stats.buckets.good} <span style={{ fontSize: 10, color: "#94a3b8" }}>({pct(stats.buckets.good)}%)</span></span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 10px" }}>
          <span style={{ color: "#d97706" }}>Developing (55–69)</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{stats.buckets.developing} <span style={{ fontSize: 10, color: "#94a3b8" }}>({pct(stats.buckets.developing)}%)</span></span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "2px 10px" }}>
          <span style={{ color: "#dc2626" }}>Coaching needed (&lt;55)</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{stats.buckets.coaching} <span style={{ fontSize: 10, color: "#94a3b8" }}>({pct(stats.buckets.coaching)}%)</span></span>
        </div>
      </div>
      <div style={{ fontSize: 10, color: "#94a3b8", padding: "6px 10px", fontStyle: "italic" }}>
        Top coaching theme — coming soon (Item C pending).
      </div>
    </div>
  );
}

// ── Main panel component ────────────────────────────────────────────────

export default function PerformanceSnapshotPanel({
  open,
  rep,            // { agent_id, agent_name, agent_email, role_breakdown, calls, connects, connect_rate, voicemails, no_answers, demos_booked, avg_talk_sec, talk_seconds, decision_maker_contacts, decision_maker_rate }
  calls,          // array of per-call rows from /admin/rep-activity/{agent_id}/calls
  callsLoading,
  callsTotal,     // Iter 342 — accurate total-in-window count from the backend, independent of the display slice
  rangeLabel,     // "Today" | "Last 7 days" | etc.
  onClose,
  onViewFullActivity,
  onSendCoachingNote,
  onOpenCallReview,   // (call) => void — pass a call row to the parent so the Call Review lightbox opens for it.
}) {
  // Iter 170.2 (Josh) — when the panel mounts/unmounts, broadcast to
  // App.js so the bottom dock (RC/AIChat/Notifications) slides left out
  // of the way (it was previously obscuring the panel footer).
  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent("rightpanel:open", { detail: { width: 500 } }));
    return () => window.dispatchEvent(new CustomEvent("rightpanel:close"));
  }, [open]);

  // Iter 170.2 (Josh) — Section 1 click-to-expand: surface the top-N
  // scored calls (descending) so a manager can see WHICH calls drove
  // the live-contacts count without leaving the panel.
  // Iter 171.B (Danny — Performance Snapshot tile drill-downs):
  // generalized this to "one expanded tile at a time" so Total calls,
  // Voicemails, No answer, and Connect rate are all drillable. The
  // legacy `setLiveContactsExpanded` is kept as an alias for the live
  // contacts case so existing test-ids still resolve.
  const [expandedTile, setExpandedTile] = useState(null);
  const liveContactsExpanded = expandedTile === "live";
  const toggleTile = (id) => setExpandedTile((cur) => (cur === id ? null : id));

  if (!open || !rep) return null;

  const live = rep.live_human_conversations ?? rep.connects ?? 0;
  const vm = rep.voicemails || 0;
  const na = rep.no_answers || 0;
  const total = rep.calls || 0;
  // Iter 176 Phase 1 — Auto-attendant / IVR count surfaced in "Other
  // contact types" stripe so reps see IVR is excluded from connect rate.
  const ivr = rep.ivr_calls || 0;
  const totalTalkSec = rep.talk_seconds || 0;
  // Iter 176 Phase 2 — Human connect rate is the canonical metric per
  // scoring_and_dialer_updates.md §2A. We prefer the new field but fall
  // back to legacy `connect_rate` for transitional clients.
  const humanConnectRate = rep.human_connect_rate ?? rep.connect_rate ?? 0;
  const avgLiveSec = rep.avg_live_conversation_sec ?? rep.avg_talk_sec ?? 0;
  const avgVoicemailSec = rep.avg_voicemail_sec ?? 0;

  return (
    <>
      {/* Backdrop — subtle so the rep activity list still shows through */}
      <div
        data-testid="snapshot-backdrop"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.18)", zIndex: 150 }}
      />
      <aside
        data-testid="performance-snapshot-panel"
        data-rep-id={rep.agent_id}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          maxWidth: "94vw",
          background: "#fff",
          borderLeft: "0.5px solid #e2e8f0",
          boxShadow: "-10px 0 30px rgba(15,23,42,0.08)",
          zIndex: 160,
          display: "flex",
          flexDirection: "column",
          fontFamily: "var(--font)",
        }}
      >
        {/* Header */}
        <header style={{ padding: "16px 20px 12px", borderBottom: "0.5px solid #e2e8f0", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Performance Snapshot</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rep.agent_email || rep.agent_name}>
                {rep.agent_name || rep.agent_email || "Unassigned"}
              </div>
              {rep.agent_email && rep.agent_email !== rep.agent_name && (
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{rep.agent_email} {rep.agent_role && <>· {rep.agent_role}</>}</div>
              )}
              <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>Showing: <strong>{rangeLabel || "Last 7 days"}</strong></div>
            </div>
            <button
              data-testid="snapshot-close-btn"
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: 4 }}
              title="Close Snapshot"
            >
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </header>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 20px" }}>

          {/* SECTION 1 — Call volume overview */}
          <section data-testid="snapshot-section-volume" style={{ marginBottom: 22 }}>
            <SectionHeader Icon={Phone} title="Call volume" subtitle="High-level totals for the selected period" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginTop: 10 }}>
              <Stat
                label="Total calls"
                value={total}
                clickable={total > 0}
                onClick={() => total > 0 && toggleTile("total")}
                expanded={expandedTile === "total"}
                testId="snapshot-total-calls-stat"
              />
              <Stat
                label="Live contacts"
                value={live}
                color="#2563eb"
                clickable={live > 0}
                onClick={() => live > 0 && toggleTile("live")}
                expanded={liveContactsExpanded}
                testId="snapshot-live-contacts-stat"
              />
              <Stat
                label="Voicemails"
                value={vm}
                color="#d97706"
                clickable={vm > 0}
                onClick={() => vm > 0 && toggleTile("voicemails")}
                expanded={expandedTile === "voicemails"}
                testId="snapshot-voicemails-stat"
              />
              <Stat
                label="No answer"
                value={na}
                color="#64748b"
                clickable={na > 0}
                onClick={() => na > 0 && toggleTile("no_answer")}
                expanded={expandedTile === "no_answer"}
                testId="snapshot-no-answer-stat"
              />
              <Stat
                label="Human connect rate"
                value={`${humanConnectRate}%`}
                color={humanConnectRate >= 30 ? "#059669" : humanConnectRate >= 15 ? "#0f172a" : "#dc2626"}
                clickable={total > 0}
                onClick={() => total > 0 && toggleTile("connect_rate")}
                expanded={expandedTile === "connect_rate"}
                testId="snapshot-connect-rate-stat"
              />
            </div>
            <CallVolumeBar connects={live} voicemails={vm} no_answers={na} total={total} />
            {/* Iter 176 Phase 1 §2C — "Other contact types" stripe so reps
                see that IVR + voicemail + no-answer calls are excluded
                from the human connect rate above. */}
            {(ivr > 0 || vm > 0 || na > 0) && (
              <div
                data-testid="snapshot-other-contact-types"
                style={{
                  marginTop: 10, padding: "8px 12px", border: "0.5px solid #e2e8f0",
                  background: "#f8fafc", borderRadius: 8, fontSize: 11, color: "#475569",
                  display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                }}
                title="These call types are not counted in the Human connect rate."
              >
                <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", letterSpacing: "0.04em", textTransform: "uppercase" }}>Other contact types · not in connect rate</span>
                {ivr > 0 && (
                  <span data-testid="snapshot-ivr-count" style={{ color: "#0d9488", fontWeight: 600 }}>
                    Auto-attendant <span style={{ fontVariantNumeric: "tabular-nums" }}>{ivr}</span>
                  </span>
                )}
                {vm > 0 && (
                  <span style={{ color: "#d97706", fontWeight: 600 }}>
                    Voicemail <span style={{ fontVariantNumeric: "tabular-nums" }}>{vm}</span>
                  </span>
                )}
                {na > 0 && (
                  <span style={{ color: "#64748b", fontWeight: 600 }}>
                    No answer <span style={{ fontVariantNumeric: "tabular-nums" }}>{na}</span>
                  </span>
                )}
              </div>
            )}
            {liveContactsExpanded && (
              <TopScoredCallsList calls={calls} onOpenCallReview={onOpenCallReview} />
            )}
            {expandedTile && expandedTile !== "live" && (
              <TileExpansion
                tile={expandedTile}
                calls={calls}
                callsLoading={callsLoading}
                callsTotal={callsTotal}
                rep={rep}
                onOpenCallReview={onOpenCallReview}
              />
            )}
          </section>

          {/* SECTION 2 — Contact role breakdown */}
          <section data-testid="snapshot-section-roles" style={{ marginBottom: 22 }}>
            <SectionHeader Icon={Users} title="Who was reached" subtitle="Live contacts grouped by role — decision-makers shown first" />
            <ContactRoleBreakdown roleBreakdown={rep.role_breakdown} connects={live} />
            {/* Iter 174.2 (Danny — granular Outcome). Sub-status tier
                breakdown derived from each call's lead.sub_status. Shows
                Gatekeeper / Administrative / Physician-Clinical /
                Executive / No live contact buckets so admins can see at
                a glance whether the rep is reaching decision-makers or
                stuck talking to front desks. */}
            <SubStatusTierBreakdown calls={calls} expectedTotal={live} />
            <div style={{ marginTop: 10, padding: "8px 10px", background: rep.decision_maker_contacts > 0 ? "#ecfdf5" : "#f8fafc", border: `0.5px solid ${rep.decision_maker_contacts > 0 ? "#a7f3d0" : "#e2e8f0"}`, borderRadius: 8, fontSize: 11, color: rep.decision_maker_contacts > 0 ? "#065f46" : "#64748b", display: "flex", justifyContent: "space-between" }}>
              <span><strong>Decision-maker contacts</strong></span>
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }} data-testid="snapshot-dm-total">{rep.decision_maker_contacts || 0} <span style={{ fontWeight: 400, color: "#64748b" }}>· {rep.decision_maker_rate || 0}% of calls</span></span>
            </div>
          </section>

          {/* SECTION 3 — Call time + duration */}
          <section data-testid="snapshot-section-duration" style={{ marginBottom: 22 }}>
            <SectionHeader Icon={BarChart3} title="Call time & duration" subtitle="Live-conversation talk-time only — voicemail & IVR excluded" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 10 }}>
              <Stat label="Total talk time" value={fmtSecs(totalTalkSec)} />
              {/* Iter 176 Phase 2 §2B — Renamed "Avg / connected call"
                  to "Avg live conversation" and computed over live human
                  calls only (excluding sub-10s rows + voicemail + IVR). */}
              <Stat label="Avg live conversation" value={fmtSecs(avgLiveSec)} testId="snapshot-avg-live-conversation-stat" />
              <Stat label="Avg / all calls" value={total ? fmtSecs(Math.round(totalTalkSec / total)) : "—"} />
            </div>
            {/* Iter 176 Phase 2 §2B — Avg voicemail duration coaching
                signal. Over 45s is too long. */}
            {avgVoicemailSec > 0 && (
              <div
                data-testid="snapshot-avg-voicemail-row"
                style={{
                  marginTop: 10, padding: "8px 12px",
                  background: avgVoicemailSec > 45 ? "#fff7ed" : "#f8fafc",
                  border: `0.5px solid ${avgVoicemailSec > 45 ? "#fdba74" : "#e2e8f0"}`,
                  borderRadius: 8, fontSize: 11,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                title="Coaching signal — voicemails should be 20-30s max. Over 45s is too long."
              >
                <span style={{ color: avgVoicemailSec > 45 ? "#c2410c" : "#475569", fontWeight: 600 }}>
                  Avg voicemail duration
                </span>
                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: avgVoicemailSec > 45 ? "#c2410c" : "#0f172a" }}>
                  {fmtSecs(avgVoicemailSec)}
                  {avgVoicemailSec > 45 && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#c2410c" }}>· trim</span>}
                </span>
              </div>
            )}
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 14, marginBottom: 4 }}>Distribution across all {total} call{total === 1 ? "" : "s"}</div>
            <DurationDistribution calls={calls} />
          </section>

          {/* SECTION 4 — Idle time (placeholder) */}
          <section data-testid="snapshot-section-idle" style={{ marginBottom: 22 }}>
            <SectionHeader Icon={Clock} title="Idle time" subtitle="Gaps between calls in the active dialer session" />
            <div style={{ marginTop: 8 }}>
              <IdleTimePlaceholder />
            </div>
          </section>

          {/* SECTION 5 — Call quality + AI scores */}
          <section data-testid="snapshot-section-quality" style={{ marginBottom: 22 }}>
            <SectionHeader Icon={Sparkles} title="Call quality & AI scores" subtitle="Agent call scores aggregated for this period" />
            <CallQualityBlock calls={calls} callsLoading={callsLoading} />
            {onSendCoachingNote && (
              <button
                data-testid="snapshot-send-coaching"
                onClick={onSendCoachingNote}
                style={{
                  marginTop: 12, padding: "8px 12px", border: "0.5px solid #c7d2fe", background: "#eef2ff", color: "#3730a3",
                  borderRadius: 8, fontSize: 11, cursor: "pointer", fontFamily: "var(--font)", fontWeight: 500,
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Sparkles style={{ width: 12, height: 12 }} /> Send coaching note →
              </button>
            )}
          </section>
        </div>

        {/* Footer */}
        <footer style={{ padding: "12px 20px", borderTop: "0.5px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: "#fafbfc" }}>
          <button
            data-testid="snapshot-close-footer"
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#64748b", fontSize: 12, cursor: "pointer", fontFamily: "var(--font)" }}
          >
            ← Close Snapshot
          </button>
          <button
            data-testid="snapshot-view-full-activity"
            onClick={onViewFullActivity}
            style={{
              padding: "7px 14px", border: "none", background: "#2563eb", color: "#fff",
              borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "var(--font)", fontWeight: 500,
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            View full activity <ChevronRight style={{ width: 13, height: 13 }} />
          </button>
        </footer>
      </aside>
    </>
  );
}

function SectionHeader({ Icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, paddingBottom: 4, borderBottom: "0.5px solid #f1f5f9" }}>
      <Icon style={{ width: 13, height: 13, color: "#64748b", marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function Stat({ label, value, color, clickable, onClick, expanded, testId }) {
  const baseStyle = {
    padding: "8px 10px",
    background: "#fff",
    border: "0.5px solid #e2e8f0",
    borderRadius: 8,
    transition: "background 140ms ease, border-color 140ms ease",
  };
  if (clickable) {
    return (
      <button
        type="button"
        data-testid={testId}
        data-expanded={expanded ? "1" : "0"}
        onClick={onClick}
        title={expanded ? "Hide details" : "Click for detailed breakdown"}
        style={{
          ...baseStyle,
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "var(--font)",
          borderColor: expanded ? "#bfdbfe" : "#e2e8f0",
          background: expanded ? "#eff6ff" : "#fff",
        }}
      >
        <div style={{ fontSize: 9, color: "#94a3b8", letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
          {label}
          <ChevronDown style={{ width: 9, height: 9, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 140ms" }} />
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: color || "#0f172a", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      </button>
    );
  }
  return (
    <div data-testid={testId} style={baseStyle}>
      <div style={{ fontSize: 9, color: "#94a3b8", letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: color || "#0f172a", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

// Iter 170.2 (Josh) — when manager clicks the Live Contacts tile, surface
// the highest-scored calls (descending) so they can see WHICH live
// contacts drove the headline number. Each row is clickable → opens the
// Call Review lightbox via the parent.
function TopScoredCallsList({ calls, onOpenCallReview }) {
  // Live contacts = call rows where outcome is "connected" OR
  // is_decision_maker is true OR outcome is in the spec's live-contact
  // bucket. We're permissive here so the list is never empty for managers
  // who tagged a role but used a non-"connected" granular outcome.
  const liveCalls = useMemo(() => {
    const isLive = (c) => {
      const o = (c.outcome || "").toLowerCase();
      return (
        o === "connected" ||
        c.is_decision_maker === true ||
        (c.contact_role && o !== "voicemail" && o !== "no_answer" && o !== "no-answer" && o !== "wrong_number" && o !== "wrong-number")
      );
    };
    return (calls || [])
      .filter((c) => c.kind === "call" || c.type === "rc_call" || c.type === "call_disposition")
      .filter(isLive)
      .map((c) => ({
        ...c,
        _score: typeof c.agent_score === "number" ? c.agent_score : -1,
      }))
      .sort((a, b) => b._score - a._score || (b.duration_sec || 0) - (a.duration_sec || 0))
      .slice(0, 8);
  }, [calls]);

  if (!liveCalls.length) {
    return (
      <div data-testid="snapshot-top-scored-empty" style={{ marginTop: 10, padding: "10px 12px", background: "#f8fafc", border: "0.5px dashed #cbd5e1", borderRadius: 8, fontSize: 11, color: "#64748b" }}>
        No live contacts in this period. Top-scored calls will surface here once reps tag a contact role on their dispositions.
      </div>
    );
  }

  const fmtScore = (s) => (typeof s === "number" && s > 0 ? Math.round(s) : "—");
  const scoreColor = (s) => {
    if (typeof s !== "number" || s <= 0) return "#94a3b8";
    if (s >= 85) return "#059669";
    if (s >= 70) return "#2563eb";
    if (s >= 55) return "#d97706";
    return "#dc2626";
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  };

  return (
    <div data-testid="snapshot-top-scored-calls" style={{ marginTop: 12, borderRadius: 8, border: "0.5px solid #e2e8f0", overflow: "hidden" }}>
      <div style={{ padding: "6px 10px", background: "#f8fafc", fontSize: 10, fontWeight: 600, color: "#64748b", letterSpacing: "0.04em", textTransform: "uppercase", borderBottom: "0.5px solid #e2e8f0" }}>
        Top {liveCalls.length} scored live contact{liveCalls.length === 1 ? "" : "s"}
      </div>
      {liveCalls.map((c, idx) => (
        <button
          type="button"
          key={c.id || c.external_id || idx}
          data-testid={`snapshot-top-call-${idx}`}
          data-agent-score={c._score}
          onClick={() => onOpenCallReview && onOpenCallReview(c)}
          title={onOpenCallReview ? "Open Call Review" : ""}
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1fr auto",
            alignItems: "center",
            gap: 10,
            padding: "8px 10px",
            background: "#fff",
            border: "none",
            borderBottom: idx === liveCalls.length - 1 ? "none" : "0.5px solid #f1f5f9",
            cursor: onOpenCallReview ? "pointer" : "default",
            fontFamily: "var(--font)",
            textAlign: "left",
            width: "100%",
          }}
        >
          <div style={{
            background: scoreColor(c._score) + "22",
            color: scoreColor(c._score),
            borderRadius: 6,
            padding: "4px 0",
            textAlign: "center",
            fontSize: 13,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
          }}>{fmtScore(c._score)}</div>
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {c.lead_name || c.lead_company || c.company || c.from_number || c.to_number || "Lead"}
            </span>
            <span style={{ fontSize: 10, color: "#64748b", display: "flex", gap: 6, alignItems: "center" }}>
              <span>{fmtDate(c.at || c.created_at)}</span>
              <span>·</span>
              <span>{(c.duration_sec || 0) >= 60 ? `${Math.round((c.duration_sec || 0) / 60)}m` : `${c.duration_sec || 0}s`}</span>
              {c.contact_role && (
                <>
                  <span>·</span>
                  <span style={{ color: colorForRole(c.contact_role), fontWeight: 500 }}>{(CONTACT_ROLES.find((r) => r.id === c.contact_role) || {}).label || c.contact_role}</span>
                  {c.is_decision_maker && <span style={{ fontSize: 8, fontWeight: 700, color: "#0F6E56" }}>DM</span>}
                </>
              )}
            </span>
          </div>
          {onOpenCallReview && <ExternalLink style={{ width: 12, height: 12, color: "#94a3b8" }} />}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Iter 171.B (Danny — Performance Snapshot tile drill-downs, 2026-06-08)
//
// Generic expansion panel for the Total / Voicemails / No answer /
// Connect rate stat tiles. Each tile reveals a different lens:
//
//   total         — counts grouped by outcome (taxonomy bar) +
//                   chronological list of every call this window.
//   voicemails    — list of voicemail rows (lead, number, time, etc.)
//   no_answer     — list of no-answer rows (same shape as voicemails).
//   connect_rate  — deep breakdown of WHO the rep reached on each
//                   connected call: physician / office mgr / owner /
//                   medical staff / other / untagged — alongside the
//                   voicemail + no-answer denominators for context.
// ─────────────────────────────────────────────────────────────────────

const TILE_TITLES = {
  total: "All calls in this window",
  voicemails: "Voicemails left",
  no_answer: "No-answer attempts",
  connect_rate: "Connect rate breakdown",
};

function TileExpansion({ tile, calls, callsLoading, callsTotal, rep, onOpenCallReview }) {
  if (callsLoading) {
    return (
      <div data-testid={`snapshot-tile-loading-${tile}`} style={{ marginTop: 12, padding: "12px 14px", background: "#f8fafc", border: "0.5px dashed #cbd5e1", borderRadius: 8, fontSize: 11, color: "#64748b" }}>
        Loading call detail…
      </div>
    );
  }

  return (
    <div data-testid={`snapshot-tile-expansion-${tile}`} style={{ marginTop: 12, borderRadius: 10, border: "0.5px solid #e2e8f0", overflow: "hidden", background: "#fff" }}>
      <div style={{ padding: "8px 12px", background: "#f8fafc", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {TILE_TITLES[tile] || tile}
        </div>
      </div>
      {tile === "total" && <TotalCallsBreakdown calls={calls} callsTotal={callsTotal} onOpenCallReview={onOpenCallReview} />}
      {tile === "voicemails" && <CallsListByOutcome calls={calls} outcome="voicemail" onOpenCallReview={onOpenCallReview} emptyLabel="No voicemails left in this window." />}
      {tile === "no_answer" && <CallsListByOutcome calls={calls} outcomes={["no_answer", "no-answer"]} onOpenCallReview={onOpenCallReview} emptyLabel="No no-answer attempts in this window." />}
      {tile === "connect_rate" && <ConnectRateBreakdown calls={calls} rep={rep} onOpenCallReview={onOpenCallReview} />}
    </div>
  );
}

function _normalizeOutcome(c) {
  return (c.outcome || "").toLowerCase().replace(/_/g, "-");
}
function _isCallRow(c) {
  return c.kind === "call" || c.type === "rc_call" || c.type === "call_disposition";
}
function _fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}
function _outcomeBg(o) {
  const n = (o || "").toLowerCase().replace(/_/g, "-");
  if (n === "connected") return { bg: "#dcfce7", color: "#15803d" };
  if (n === "voicemail") return { bg: "#fef3c7", color: "#92400e" };
  if (n === "no-answer") return { bg: "#f1f5f9", color: "#475569" };
  if (n === "busy") return { bg: "#fee2e2", color: "#991b1b" };
  if (n === "wrong-number") return { bg: "#fce7f3", color: "#9d174d" };
  return { bg: "#e0e7ff", color: "#3730a3" };
}

function TotalCallsBreakdown({ calls, callsTotal, onOpenCallReview }) {
  const callRows = (calls || []).filter(_isCallRow);
  const displayed = callRows.length;
  // Iter 342 — when the backend supplies an accurate `callsTotal` from
  // its full-window aggregate, prefer that over the paginated
  // callRows.length. Fallback keeps behavior identical for pre-Iter-342
  // clients or if the parent forgets to pass callsTotal.
  const total = typeof callsTotal === "number" && callsTotal > 0 ? callsTotal : displayed;
  const byOutcome = useMemo(() => {
    const map = {};
    callRows.forEach((c) => {
      const o = _normalizeOutcome(c) || "untagged";
      map[o] = (map[o] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [callRows]);

  if (!total) {
    return <div style={{ padding: 14, fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No calls in this window.</div>;
  }

  return (
    <div>
      <div style={{ padding: "12px 14px", borderBottom: "0.5px solid #f1f5f9" }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
          Outcome split ({total} call{total === 1 ? "" : "s"})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {byOutcome.map(([o, n]) => {
            const { bg, color } = _outcomeBg(o);
            const pc = Math.round((n / total) * 100);
            return (
              <span key={o} data-testid={`snapshot-total-outcome-${o}`} style={{ padding: "4px 9px", borderRadius: 12, background: bg, color, fontSize: 11, fontWeight: 600 }}>
                {o.replace(/-/g, " ")} · <strong>{n}</strong> <span style={{ fontWeight: 400, opacity: 0.7 }}>({pc}%)</span>
              </span>
            );
          })}
        </div>
      </div>
      <CallsRowList rows={callRows.slice(0, 50)} onOpenCallReview={onOpenCallReview} testIdPrefix="snapshot-total-call" />
      {displayed > 50 || total > displayed ? (
        <div style={{ padding: "8px 12px", fontSize: 10, color: "#94a3b8", fontStyle: "italic", borderTop: "0.5px solid #f1f5f9" }}>
          Showing first {Math.min(50, displayed).toLocaleString()} of {total.toLocaleString()}. Use &quot;View full activity &rarr;&quot; for the complete list.
        </div>
      ) : null}
    </div>
  );
}

function CallsListByOutcome({ calls, outcome, outcomes, onOpenCallReview, emptyLabel }) {
  const set = new Set((outcomes || (outcome ? [outcome] : [])).map((o) => o.toLowerCase().replace(/_/g, "-")));
  const rows = (calls || []).filter(_isCallRow).filter((c) => set.has(_normalizeOutcome(c)));
  if (!rows.length) {
    return <div style={{ padding: 14, fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>{emptyLabel || "No matching calls."}</div>;
  }
  return <CallsRowList rows={rows} onOpenCallReview={onOpenCallReview} testIdPrefix={`snapshot-${outcome || (outcomes && outcomes[0]) || "row"}-call`} />;
}

function CallsRowList({ rows, onOpenCallReview, testIdPrefix }) {
  return (
    <div>
      {rows.map((c, idx) => {
        const { bg, color } = _outcomeBg(c.outcome);
        // Iter 343 — hover-tooltip preview so admins can scan disposition
        // context without opening the full lightbox.
        const tParts = [];
        if (c.outcome) tParts.push(`Outcome: ${c.outcome}${c.outcome_detail ? ` (${c.outcome_detail})` : ""}`);
        if (c.call_type) tParts.push(`Call type: ${c.call_type}`);
        if (c.next_step) tParts.push(`Next step: ${c.next_step}`);
        if (Array.isArray(c.product_tags) && c.product_tags.length) tParts.push(`Products: ${c.product_tags.join(", ")}`);
        if (c.substatus) tParts.push(`Sub-status: ${c.substatus}`);
        if (c.duration_sec) tParts.push(`Duration: ${Math.floor(c.duration_sec / 60)}m ${c.duration_sec % 60}s`);
        if (c.notes_preview) tParts.push(`Note: ${c.notes_preview}`);
        const tooltip = tParts.length ? tParts.join("\n") : (c.outcome || "");
        return (
          <button
            type="button"
            key={c.id || c.external_id || idx}
            data-testid={`${testIdPrefix}-${idx}`}
            title={tooltip}
            onClick={() => onOpenCallReview && onOpenCallReview(c)}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 10,
              alignItems: "center",
              width: "100%",
              padding: "8px 12px",
              background: "#fff",
              border: "none",
              borderBottom: idx === rows.length - 1 ? "none" : "0.5px solid #f1f5f9",
              cursor: onOpenCallReview ? "pointer" : "default",
              fontFamily: "var(--font)",
              textAlign: "left",
            }}
          >
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.lead_name || c.lead_company || c.company || c.from_number || c.to_number || "Lead"}
              </span>
              <span style={{ fontSize: 10, color: "#64748b" }}>
                {_fmtDate(c.at || c.created_at)} &middot; {(c.duration_sec || 0) >= 60 ? `${Math.round((c.duration_sec || 0) / 60)}m` : `${c.duration_sec || 0}s`}
                {c.contact_role && (
                  <span style={{ marginLeft: 6, color: colorForRole(c.contact_role), fontWeight: 500 }}>
                    &middot; {(CONTACT_ROLES.find((r) => r.id === c.contact_role) || {}).label || c.contact_role}
                  </span>
                )}
              </span>
            </div>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: bg, color, textTransform: "uppercase", letterSpacing: "0.03em" }}>
              {(c.outcome || "—").toString().replace(/[-_]/g, " ")}
            </span>
            {onOpenCallReview && <ExternalLink style={{ width: 12, height: 12, color: "#94a3b8" }} />}
          </button>
        );
      })}
    </div>
  );
}

function ConnectRateBreakdown({ calls, rep, onOpenCallReview }) {
  const callRows = (calls || []).filter(_isCallRow);
  const total = callRows.length;
  const connected = callRows.filter((c) => _normalizeOutcome(c) === "connected");
  const voicemails = callRows.filter((c) => _normalizeOutcome(c) === "voicemail").length;
  const noAnswers = callRows.filter((c) => _normalizeOutcome(c) === "no-answer").length;

  const roleTally = useMemo(() => {
    const t = {};
    connected.forEach((c) => {
      const role = c.contact_role || "untagged";
      t[role] = (t[role] || 0) + 1;
    });
    return SNAPSHOT_DISPLAY_ORDER
      .map((id) => {
        const meta = CONTACT_ROLES.find((r) => r.id === id);
        return { id, label: meta?.label || id, color: colorForRole(id), count: t[id] || 0 };
      })
      .filter((row) => row.count > 0);
  }, [connected]);

  // Iter 171.G (Danny §4): aggregate two derived metrics from the
  // role tally — Decision-maker rate (Physician/Owner/Office Mgr/
  // Practice Admin/Executive/Billing-RCM) and Gatekeeper rate
  // (Front Desk/MA). These predict pipeline movement better than raw
  // connect rate alone.
  const DECISION_MAKER_ROLES = new Set([
    "physician", "physician_owner", "owner", "owner_partner",
    "office_manager", "practice_admin", "practice_administrator",
    "executive", "c_suite", "billing_rcm", "billing", "rcm",
  ]);
  const GATEKEEPER_ROLES = new Set([
    "front_desk", "ma", "medical_assistant", "receptionist",
  ]);
  const decisionMakerCount = roleTally.reduce(
    (sum, r) => sum + (DECISION_MAKER_ROLES.has(r.id) ? r.count : 0), 0);
  const gatekeeperCount = roleTally.reduce(
    (sum, r) => sum + (GATEKEEPER_ROLES.has(r.id) ? r.count : 0), 0);
  const dmRate = connected.length ? Math.round((decisionMakerCount / connected.length) * 100) : 0;
  const gkRate = connected.length ? Math.round((gatekeeperCount / connected.length) * 100) : 0;

  if (!total) {
    return <div style={{ padding: 14, fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No call data in this window.</div>;
  }

  const connectRate = rep?.connect_rate || (total ? Math.round((connected.length / total) * 100) : 0);
  const vmRate = total ? Math.round((voicemails / total) * 100) : 0;
  const naRate = total ? Math.round((noAnswers / total) * 100) : 0;
  const otherCount = total - connected.length - voicemails - noAnswers;

  return (
    <div data-testid="snapshot-connect-rate-breakdown">
      <div style={{ padding: "12px 14px", borderBottom: "0.5px solid #f1f5f9", fontSize: 11, color: "#475569" }}>
        <strong style={{ color: "#0f172a" }}>{connectRate}%</strong> connect rate = <strong>{connected.length}</strong> live &divide; <strong>{total}</strong> total calls
      </div>

      <div style={{ padding: "12px 14px", borderBottom: "0.5px solid #f1f5f9" }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
          Where the other calls went
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <DenominatorTile label="Voicemail" value={voicemails} pct={vmRate} color="#92400e" bg="#fef3c7" testId="connect-rate-vm" />
          <DenominatorTile label="No answer" value={noAnswers} pct={naRate} color="#475569" bg="#f1f5f9" testId="connect-rate-na" />
          <DenominatorTile label="Other (busy / wrong #)" value={otherCount > 0 ? otherCount : 0} pct={total ? Math.round((Math.max(otherCount, 0) / total) * 100) : 0} color="#991b1b" bg="#fee2e2" testId="connect-rate-other" />
        </div>
      </div>

      <div style={{ padding: "12px 14px" }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
          Connected calls by contact role ({connected.length})
        </div>
        {roleTally.length === 0 ? (
          <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
            No connected calls yet &mdash; once reps tag a role on the disposition modal it&apos;ll surface here.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {roleTally.map((row) => {
              const pc = connected.length ? Math.round((row.count / connected.length) * 100) : 0;
              return (
                <div key={row.id} data-testid={`connect-rate-role-${row.id}`} style={{ display: "grid", gridTemplateColumns: "1fr 60px 50px", gap: 10, alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 4, background: row.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#0f172a", fontWeight: 500 }}>{row.label}</span>
                  </div>
                  <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pc}%`, background: row.color }} />
                  </div>
                  <div style={{ fontSize: 11, color: "#475569", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                    <strong style={{ color: "#0f172a" }}>{row.count}</strong> <span style={{ color: "#94a3b8" }}>&middot; {pc}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter 171.G (Danny §4): Decision-maker vs Gatekeeper aggregate
          metrics — surface the two predictive rates so managers can
          see at a glance whether the rep is breaking through to
          decision-makers or stalling at the front desk. */}
      {connected.length > 0 && (
        <div data-testid="connect-rate-dm-gk-summary" style={{ padding: "10px 14px", borderTop: "0.5px solid #f1f5f9", background: "#fafafa", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div data-testid="connect-rate-dm-summary" style={{ padding: "8px 10px", background: "#dcfce7", borderRadius: 8, border: "0.5px solid #86efac" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.04em" }}>Decision-maker contact rate</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#15803d", marginTop: 3 }}>
              {decisionMakerCount} <span style={{ fontSize: 10, fontWeight: 500, color: "#16a34a" }}>of {connected.length} ({dmRate}%)</span>
            </div>
            <div style={{ fontSize: 9, color: "#15803d", opacity: 0.75, marginTop: 2 }}>Physician · Office Mgr · Admin · Exec · Billing/RCM</div>
          </div>
          <div data-testid="connect-rate-gk-summary" style={{ padding: "8px 10px", background: "#fef3c7", borderRadius: 8, border: "0.5px solid #fcd34d" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.04em" }}>Gatekeeper contact rate</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#92400e", marginTop: 3 }}>
              {gatekeeperCount} <span style={{ fontSize: 10, fontWeight: 500, color: "#a16207" }}>of {connected.length} ({gkRate}%)</span>
            </div>
            <div style={{ fontSize: 9, color: "#92400e", opacity: 0.75, marginTop: 2 }}>Front Desk · MA only — not authorised to decide</div>
          </div>
        </div>
      )}

      {connected.length > 0 && (
        <div style={{ borderTop: "0.5px solid #f1f5f9" }}>
          <div style={{ padding: "8px 14px", fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Connected calls (newest first)
          </div>
          <CallsRowList rows={connected.slice(0, 20)} onOpenCallReview={onOpenCallReview} testIdPrefix="snapshot-connect-call" />
        </div>
      )}
    </div>
  );
}

function DenominatorTile({ label, value, pct, color, bg, testId }) {
  return (
    <div data-testid={testId} style={{ padding: "8px 10px", borderRadius: 8, background: bg, border: `0.5px solid ${color}30` }}>
      <div style={{ fontSize: 9, fontWeight: 600, color, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      <div style={{ fontSize: 10, color, opacity: 0.75, marginTop: 1 }}>{pct}% of total</div>
    </div>
  );
}
